import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { routes } from '@/lib/routes';
import { ownerApi } from '@/features/workspace/api';
import { WorkspaceModulePage } from '@/features/workspace/WorkspaceModulePage';
import AdminUsersPage from '@/features/owner/components/AdminUsersPage';
import { OwnerHomeCabinet } from '@/features/owner/components/OwnerHomeCabinet';
import { Owner10HubShell } from '@/features/owner/components/Owner10HubShell';
import { DecisionsAuditPage } from '@/features/owner/components/DecisionsAuditPage';
import { MeetingsDutyPage } from '@/features/owner/components/MeetingsDutyPage';
import { OwnerInstructionPage } from '@/features/owner/components/OwnerInstructionPage';
import { OwnerAnnouncementsPage } from '@/features/owner/components/OwnerAnnouncementsPage';
import { OwnerDoctoralMonitoringPage } from '@/features/owner/components/OwnerDoctoralMonitoringPage';
import { OwnerSourceDocumentsPage } from '@/features/owner/components/OwnerSourceDocumentsPage';
import { OwnerSuperAiPage } from '@/features/owner/components/OwnerSuperAiPage';
import { WorkRequestsHub } from '@/features/work-requests';
import { CommunicationHub } from '@/features/communications';
import { OwnerContentCenter } from '@/features/owner/components/OwnerContentCenter';
import CoverageCatalogPage from '@/features/owner/coverage/CoverageCatalogPage';
import { ResearchToolsPage } from '@/features/research-tools';
import { OrdersPublicationsPage } from '@/features/orders-publications';
import { FileHubPage } from '@/features/file-hub';
import type { WorkspaceSummary } from '@/features/workspace/types';

const OwnerPage = () => {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const { t } = useI18n();
  const [summary, setSummary] = useState<WorkspaceSummary | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const data = await ownerApi.workspace();
          if (!cancelled) {
            setSummary(data);
            setError(false);
          }
          return;
        } catch {
          await new Promise((resolve) => setTimeout(resolve, 250 * (attempt + 1)));
        }
      }
      if (!cancelled) setError(true);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!hasRole('SYSTEM_OWNER')) {
    return <Navigate to={routes.denied} replace />;
  }

  if (error) {
    return (
      <section className="owner-hub-entry ov-premium-owner">
        <div className="owner-hub-module" style={{ padding: 24 }}>
          <h2>Кабинет временно недоступен</h2>
          <p style={{ color: '#8faebd' }}>Не удалось загрузить кабинет. Обновите страницу.</p>
          <button type="button" className="owner-hub-close" onClick={() => window.location.reload()}>
            Обновить
          </button>
        </div>
      </section>
    );
  }

  if (!summary) {
    return <section className="owner-hub-entry ov-premium-owner" />;
  }

  return (
    <div className="ov-premium-owner">
      <Routes>
        <Route index element={<OwnerHomeCabinet />} />
        <Route
          path="access"
          element={
            <AdminUsersPage
              module={summary.modules.find((item) => item.key === 'access')}
              breadcrumb={t('workspace.owner.breadcrumb')}
              embedded
              onClose={() => navigate(routes.owner)}
              onBack={() => navigate(routes.owner)}
            />
          }
        />
        {/* alias: some links navigate to /owner/users instead of /owner/access */}
        <Route
          path="users"
          element={
            <AdminUsersPage
              module={summary.modules.find((item) => item.key === 'access')}
              breadcrumb={t('workspace.owner.breadcrumb')}
              embedded
              onClose={() => navigate(routes.owner)}
              onBack={() => navigate(routes.owner)}
            />
          }
        />
        <Route
          path="requests/*"
          element={
            <WorkRequestsHub
              roleContext="SYSTEM_OWNER"
              basePath={`${routes.owner}/requests`}
            />
          }
        />
        <Route
          path="communications/*"
          element={<CommunicationHub onClose={() => navigate(routes.owner)} />}
        />
        <Route path="decisions" element={<DecisionsAuditPage />} />
        <Route path="decisions/*" element={<DecisionsAuditPage />} />
        <Route path="meetings" element={<MeetingsDutyPage onClose={() => navigate(routes.owner)} />} />
        <Route path="meetings/*" element={<MeetingsDutyPage onClose={() => navigate(routes.owner)} />} />
        <Route
          path="content"
          element={
            <Owner10HubShell
              title="Публичный контент (deep-link)"
              subtitle="Не входит в сетку 10 карточек. Карточка 07 — «Объявления и обновления»."
              onClose={() => navigate(routes.owner)}
            >
              <OwnerContentCenter />
            </Owner10HubShell>
          }
        />
        <Route path="announcements" element={<OwnerAnnouncementsPage />} />
        <Route path="doctoral-monitoring" element={<OwnerDoctoralMonitoringPage />} />
        <Route path="super-ai" element={<OwnerSuperAiPage />} />
        <Route path="files/source" element={<OwnerSourceDocumentsPage />} />
        <Route
          path="coverage/*"
          element={
            <CoverageCatalogPage
              onClose={() => navigate(routes.owner)}
              onBack={() => navigate(routes.owner)}
            />
          }
        />
        <Route
          path="tools/*"
          element={
            <ResearchToolsPage onClose={() => navigate(routes.owner)} onBack={() => navigate(routes.owner)} />
          }
        />
        <Route
          path="research-tools/*"
          element={
            <ResearchToolsPage onClose={() => navigate(routes.owner)} onBack={() => navigate(routes.owner)} />
          }
        />
        <Route
          path="orders/*"
          element={<OrdersPublicationsPage onClose={() => navigate(routes.owner)} />}
        />
        <Route
          path="files/*"
          element={
            <FileHubPage onClose={() => navigate(routes.owner)} onBack={() => navigate(routes.owner)} />
          }
        />
        <Route path="instruction" element={<OwnerInstructionPage />} />
        <Route
          path=":moduleKey"
          element={
            <div className="owner-hub-page">
              <div className="owner-hub-module">
                <WorkspaceModulePage
                  modules={summary.modules}
                  basePath={routes.owner}
                  loadModule={(key, params) =>
                    ownerApi.module(key, {
                      tab: params.tab,
                      filter: params.filter,
                      search: params.search || undefined,
                    })
                  }
                />
              </div>
            </div>
          }
        />
      </Routes>
    </div>
  );
};

export default OwnerPage;
